// Questions.jsx

// Usable en: modal de nueva encuesta, formulario de edición, lista de preguntas.

import {
  useState, useEffect, useCallback, useMemo, useRef, memo,
} from 'react';
import { nanoid } from 'nanoid';
import {
  Box, TextField, Button, Typography, Card, CardContent,
  Tooltip, IconButton, FormControl, InputLabel, Select, MenuItem,
  FormControlLabel, Divider, RadioGroup, Radio, FormLabel, FormHelperText, Chip,
  Switch, LinearProgress,
} from '@mui/material';
import AddCircleOutlineIcon   from '@mui/icons-material/AddCircleOutline';
import DeleteIcon             from '@mui/icons-material/Delete';
import HelpOutlineIcon        from '@mui/icons-material/HelpOutline';
import ArrowUpwardIcon        from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon      from '@mui/icons-material/ArrowDownward';
import CloseIcon              from '@mui/icons-material/Close';
import VisibilityIcon         from '@mui/icons-material/Visibility';
import ImageIcon              from '@mui/icons-material/Image';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AutoAwesomeIcon        from '@mui/icons-material/AutoAwesome';
import LockIcon               from '@mui/icons-material/Lock';
import StarIcon               from '@mui/icons-material/Star';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../../config/firebase';
import { Emogiface, Emogiface2, Emogiface3, Emogiface4, Emogiface5, Emogiface6 } from '../../../../tools/MyIcons';
import { compressQuestionImage } from './QuestionsCompressImg';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTES EXPORTADAS
// ─────────────────────────────────────────────────────────────────────────────

export const INITIAL_QUESTION = {
  type: 'emojis',
  text: '',
  required: false,
  orden: 1,
  url: '',
};

export const EMOJI_TYPES = new Set([
  'emojis',
  'emojis2',
  'emojisCat',
  'emojispeople',
  'emojisnumbers',
  'emojislike',
]);

export const EMOJI_ICONS = {
  emojis:        Emogiface,
  emojis2:       Emogiface2,
  emojisCat:     Emogiface3,
  emojispeople:  Emogiface4,
  emojisnumbers: Emogiface5,
  emojislike:    Emogiface6,
};

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS EXPORTADOS
// ─────────────────────────────────────────────────────────────────────────────

export const getNextQuestionId = (questions) => {
  let max = 0;
  for (const key of Object.keys(questions || {})) {
    const n = Number(key.slice(1));
    if (!Number.isNaN(n)) max = Math.max(max, n);
  }
  return `q${max + 1}`;
};

export const reorderQuestions = (questions, removedOrder) => {
  const next = {};
  for (const [qid, q] of Object.entries(questions)) {
    const newOrden = q.orden > removedOrder ? q.orden - 1 : q.orden;
    next[qid] = newOrden === q.orden ? q : { ...q, orden: newOrden };
  }
  return next;
};

export const getNextOptionId = (options) =>
  options.length > 0 ? Math.max(...options.map((o) => Number(o.id) || 0)) + 1 : 1;

// Helper interno (no necesita exportarse pero se reutiliza en este archivo)
const getQuestionError = (errors, id, field) => errors?.questions?.[id]?.[field];

// ─────────────────────────────────────────────────────────────────────────────
// SUBCOMPONENTES INTERNOS
// ─────────────────────────────────────────────────────────────────────────────

const EmojiPreview = memo(({ emojiType }) => {
  const emojiSet = EMOJI_ICONS[emojiType];
  if (!emojiSet) return null;
  return (
    <Box
      component="img"
      src={emojiSet.face3}
      alt="emoji preview"
      sx={{ width: 25, height: 25, objectFit: 'contain', ml: 0.5 }}
    />
  );
});
EmojiPreview.displayName = 'EmojiPreview';

const OptionEdit = memo(({ option, index, qId, handleRemove, handleMarkChanged, inputRefs, errors, t }) => {
  const optionRef = useRef(null);

  useEffect(() => {
    const key = `option_text_${qId}_${option.id}`;
    inputRefs.current[key] = optionRef.current;
    return () => delete inputRefs.current[key];
  }, [inputRefs, qId, option.id]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
      <Radio disabled size="small" />
      <TextField
        size="small"
        inputRef={optionRef}
        defaultValue={option.text}
        onChange={handleMarkChanged}
        placeholder={t.optionPlaceholder.replace('{index}', index + 1)}
        fullWidth
        variant="outlined"
        sx={{ ml: 1 }}
        inputProps={{ maxLength: 100 }}
        error={!!getQuestionError(errors, qId, 'options')}
      />
      <IconButton
        size="small"
        color="error"
        onClick={() => handleRemove(option.id)}
        sx={{ ml: 1 }}
        aria-label={t.removeOptionAriaLabel}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
});
OptionEdit.displayName = 'OptionEdit';

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTES EXPORTADOS — usables en modal, edición y lista
// ─────────────────────────────────────────────────────────────────────────────

export const RadioOptionsEdit = memo(({
  qId, options = [], handleOptionChange, errors, inputRefs, handleMarkChanged, t,
}) => {
  const newOptionRef = useRef(null);

  const handleAddOption = useCallback(() => {
    const trimmed = newOptionRef.current?.value?.trim();
    if (!trimmed) return;
    const nextId = getNextOptionId(options);
    handleOptionChange(qId, 'options', [...options, { id: nextId, text: trimmed }]);
    newOptionRef.current.value = '';
    newOptionRef.current.focus();
  }, [qId, options, handleOptionChange]);

  const handleRemoveOption = useCallback(
    (optionId) => handleOptionChange(qId, 'options', options.filter((o) => o.id !== optionId)),
    [qId, options, handleOptionChange],
  );

  const handleKeyDown = useCallback(
    (e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddOption(); } },
    [handleAddOption],
  );

  return (
    <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid #e0e0e0' }}>
      <Typography variant="subtitle2" gutterBottom color="primary">{t.radioOptionsTitle}</Typography>
      {options.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
          {t.radioOptionsEmpty}
        </Typography>
      )}
      {options.map((option, index) => (
        <OptionEdit
          key={option.id}
          option={option}
          index={index}
          qId={qId}
          handleRemove={handleRemoveOption}
          handleMarkChanged={handleMarkChanged}
          inputRefs={inputRefs}
          errors={errors}
          t={t}
        />
      ))}
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
        <TextField
          size="small"
          inputRef={newOptionRef}
          defaultValue=""
          onKeyDown={handleKeyDown}
          onChange={handleMarkChanged}
          placeholder={t.newOptionPlaceholder}
          fullWidth
          variant="outlined"
          inputProps={{ maxLength: 100 }}
        />
        <Button
          startIcon={<AddCircleOutlineIcon />}
          onClick={handleAddOption}
          size="small"
          variant="outlined"
          sx={{ ml: 1, whiteSpace: 'nowrap' }}
        >
          {t.addButton}
        </Button>
      </Box>
      {getQuestionError(errors, qId, 'options') && (
        <FormHelperText error>{getQuestionError(errors, qId, 'options')}</FormHelperText>
      )}
    </Box>
  );
});
RadioOptionsEdit.displayName = 'RadioOptionsEdit';

export const SliderConfig = memo(({ qId, errors, t }) => (
  <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid #e0e0e0' }}>
    <Typography variant="subtitle2" gutterBottom color="primary">{t.sliderConfigTitle}</Typography>
    <Typography variant="body2" color="text.secondary">{t.sliderRange5}</Typography>
    {getQuestionError(errors, qId, 'sliderConfig') && (
      <FormHelperText error>{getQuestionError(errors, qId, 'sliderConfig')}</FormHelperText>
    )}
  </Box>
));
SliderConfig.displayName = 'SliderConfig';

export const EmojiTextConfig = memo(({
  qId, config, handleEmojiConfigChange, errors, inputRefs, handleMarkChanged, t,
}) => {
  const leftRef  = useRef(null);
  const rightRef = useRef(null);
  const showLabels = config?.show !== false;

  useEffect(() => {
    if (showLabels) {
      inputRefs.current[`config_left_${qId}`]  = leftRef.current;
      inputRefs.current[`config_right_${qId}`] = rightRef.current;
      return () => {
        delete inputRefs.current[`config_left_${qId}`];
        delete inputRefs.current[`config_right_${qId}`];
      };
    }
  }, [inputRefs, qId, showLabels]);

  const handleToggleShow = useCallback(
    (e) => {
      const newShow = e.target.checked;
      if (!newShow && showLabels) {
        const left  = leftRef.current?.value?.trim()  || config?.left  || t.defaultEmojiConfig.left;
        const right = rightRef.current?.value?.trim() || config?.right || t.defaultEmojiConfig.right;
        handleEmojiConfigChange(qId, 'config', { show: newShow, left, right });
      } else {
        handleEmojiConfigChange(qId, 'config', { ...(config || t.defaultEmojiConfig), show: newShow });
      }
    },
    [qId, config, handleEmojiConfigChange, showLabels, t],
  );

  return (
    <Box sx={{ mt: 2, p: 2, borderRadius: 1, border: '1px solid #e0e0e0' }}>
      <Typography variant="subtitle2" gutterBottom color="primary">{t.emojiConfigTitle}</Typography>
      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={<Switch checked={showLabels} onChange={handleToggleShow} color="primary" size="small" />}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <VisibilityIcon fontSize="small" />
              <Typography variant="body2">{t.showTextLabel}</Typography>
              <Tooltip title={t.showTextTooltip}>
                <HelpOutlineIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </Tooltip>
            </Box>
          }
          sx={{ m: 0 }}
        />
      </Box>
      {showLabels && (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small" label={t.leftTextLabel} inputRef={leftRef}
            defaultValue={config?.left || t.defaultEmojiConfig.left}
            onChange={handleMarkChanged}
            placeholder={t.leftTextPlaceholder} fullWidth variant="outlined"
            helperText={t.leftTextHelper}
            inputProps={{ maxLength: 50 }}
            error={!!getQuestionError(errors, qId, 'config')}
          />
          <Typography variant="body2" color="text.secondary">↔</Typography>
          <TextField
            size="small" label={t.rightTextLabel} inputRef={rightRef}
            defaultValue={config?.right || t.defaultEmojiConfig.right}
            onChange={handleMarkChanged}
            placeholder={t.rightTextPlaceholder} fullWidth variant="outlined"
            helperText={t.rightTextHelper}
            inputProps={{ maxLength: 50 }}
            error={!!getQuestionError(errors, qId, 'config')}
          />
        </Box>
      )}
      {getQuestionError(errors, qId, 'config') && (
        <FormHelperText error>{getQuestionError(errors, qId, 'config')}</FormHelperText>
      )}
    </Box>
  );
});
EmojiTextConfig.displayName = 'EmojiTextConfig';

// ─────────────────────────────────────────────────────────────────────────────
// IMAGE UPLOAD
// ─────────────────────────────────────────────────────────────────────────────
// La compresión de imagen (compressQuestionImage) vive ahora en ./QuestionsCompressImg

const QuestionImageUploadInner = memo(({ qId, imageUrl = '', onImageChange, t }) => {
  const [expanded, setExpanded]   = useState(Boolean(imageUrl));
  const [preview, setPreview]     = useState(imageUrl || null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress]   = useState(0);
  const [dragOver, setDragOver]   = useState(false);
  const inputId = `q-img-${qId}`;

  useEffect(() => {
    setPreview(imageUrl || null);
    if (imageUrl) setExpanded(true);
  }, [imageUrl]);

  const handleToggle = useCallback(() => setExpanded((v) => !v), []);

  const processFile = useCallback(async (file) => {
    if (!file) return;
    setUploading(true);
    setProgress(0);
    try {
      const blob     = await compressQuestionImage(file, 300);
      const localUrl = URL.createObjectURL(blob);
      setPreview(localUrl);
      const storageRef = ref(storage, `SurveyQuestion/${nanoid()}.jpg`);
      const task       = uploadBytesResumable(storageRef, blob, { contentType: 'image/jpeg' });
      task.on(
        'state_changed',
        (snap) => setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
        (err)  => { console.error('Error subiendo imagen de pregunta:', err); setUploading(false); },
        async () => {
          const downloadUrl = await getDownloadURL(task.snapshot.ref);
          URL.revokeObjectURL(localUrl);
          setPreview(downloadUrl);
          onImageChange(downloadUrl);
          setUploading(false);
        },
      );
    } catch (err) {
      console.error('Error procesando imagen de pregunta:', err);
      setUploading(false);
    }
  }, [onImageChange]);

  const handleFile = useCallback(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    processFile(file);
  }, [processFile]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    const file = e.dataTransfer.files?.[0];
    if (file && (file.type === 'image/jpeg' || file.type === 'image/png')) processFile(file);
  }, [uploading, processFile]);

  const handleDragOver  = useCallback((e) => { e.preventDefault(); if (!uploading) setDragOver(true); }, [uploading]);
  const handleDragLeave = useCallback(() => setDragOver(false), []);

  const handleRemove = useCallback(() => {
    setPreview(null);
    onImageChange('');
  }, [onImageChange]);

  return (
    <Box sx={{ mb: 2, borderRadius: 2, border: '1.5px solid', borderColor: preview ? 'success.400' : expanded ? 'primary.400' : 'primary.200', overflow: 'hidden', transition: 'border-color 0.25s, box-shadow 0.3s', boxShadow: expanded ? preview ? '0 3px 14px rgba(46,125,50,0.14)' : '0 3px 14px rgba(25,118,210,0.12)' : '0 1px 4px rgba(25,118,210,0.07)', background: expanded ? 'white' : 'linear-gradient(135deg, rgba(25,118,210,0.03) 0%, rgba(66,165,245,0.05) 100%)' }}>
      <Box
        onClick={!uploading ? handleToggle : undefined}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, background: expanded ? preview ? 'linear-gradient(135deg, rgba(46,125,50,0.08) 0%, rgba(76,175,80,0.12) 100%)' : 'linear-gradient(135deg, rgba(25,118,210,0.08) 0%, rgba(66,165,245,0.13) 100%)' : 'transparent', cursor: uploading ? 'default' : 'pointer', userSelect: 'none', transition: 'background 0.25s', '&:hover': !uploading ? { background: preview ? 'linear-gradient(135deg, rgba(46,125,50,0.13) 0%, rgba(76,175,80,0.18) 100%)' : 'linear-gradient(135deg, rgba(25,118,210,0.12) 0%, rgba(66,165,245,0.18) 100%)' } : {} }}
      >
        <Box sx={{ width: 28, height: 28, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: preview ? 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)' : expanded ? 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)' : 'linear-gradient(135deg, #90caf9 0%, #1976d2 100%)', boxShadow: expanded ? '0 2px 6px rgba(25,118,210,0.3)' : 'none', transition: 'all 0.25s' }}>
          <ImageIcon sx={{ color: 'white', fontSize: 16 }} />
        </Box>
        <Typography variant="caption" fontWeight={700} sx={{ flexGrow: 1, color: preview ? 'success.700' : expanded ? 'primary.700' : 'primary.main', fontSize: '0.75rem', letterSpacing: 0.2 }}>
          {t.imageUploadTitle}
        </Typography>
        {preview && !uploading && (
          <Chip size="small" icon={<CheckCircleOutlineIcon sx={{ fontSize: '0.7rem !important' }} />} label={t.imageWithLabel}
            sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: 'success.50', color: 'success.700', border: '1px solid', borderColor: 'success.300', '& .MuiChip-icon': { color: 'success.600', ml: 0.5 }, '& .MuiChip-label': { px: 0.75 } }} />
        )}
        {!preview && !uploading && (
          <Typography variant="caption" sx={{ color: 'primary.400', fontSize: '0.65rem', fontStyle: 'italic', mr: 0.5 }}>
            {expanded ? t.imageUploadHide : t.imageUploadShow}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.25s ease', color: expanded ? 'primary.main' : 'text.disabled' }}>
          <ArrowDownwardIcon sx={{ fontSize: 16 }} />
        </Box>
      </Box>

      {expanded && (
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {preview ? (
            <Box sx={{ position: 'relative', display: 'inline-block', mb: 1.5 }}>
              <Box component="img" src={preview} alt={t.imageUploadTitle}
                sx={{ maxWidth: 300, maxHeight: 200, width: 'auto', height: 'auto', borderRadius: 2, display: 'block', boxShadow: '0 6px 20px rgba(0,0,0,0.20)', objectFit: 'contain', border: '2px solid', borderColor: 'success.200' }} />
              {uploading && (
                <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 100%)', borderRadius: '0 0 10px 10px', p: 1 }}>
                  <LinearProgress variant="determinate" value={progress}
                    sx={{ height: 7, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.2)', '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #00e676 0%, #69f0ae 100%)', borderRadius: 4 } }} />
                  <Typography variant="caption" sx={{ color: 'white', display: 'block', textAlign: 'center', mt: 0.5, fontWeight: 600 }}>
                    {t.imageUploading} {progress}%
                  </Typography>
                </Box>
              )}
              {!uploading && (
                <Tooltip title={t.imageRemove}>
                  <IconButton size="small" onClick={handleRemove}
                    sx={{ position: 'absolute', top: -10, right: -10, bgcolor: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.20)', border: '1.5px solid', borderColor: 'error.200', color: 'error.main', '&:hover': { bgcolor: 'error.main', color: 'white', borderColor: 'error.main', transform: 'scale(1.1)' }, transition: 'all 0.18s' }}>
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ) : (
            <Box onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}
              sx={{ width: '100%', maxWidth: 320, mb: 1.5, p: 3, borderRadius: 2.5, border: '2px dashed', borderColor: dragOver ? 'primary.main' : 'primary.200', background: dragOver ? 'linear-gradient(135deg, rgba(25,118,210,0.10) 0%, rgba(66,165,245,0.15) 100%)' : 'linear-gradient(135deg, rgba(25,118,210,0.03) 0%, rgba(66,165,245,0.06) 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, transition: 'all 0.2s ease', transform: dragOver ? 'scale(1.01)' : 'scale(1)' }}>
              <Box sx={{ width: 52, height: 52, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: dragOver ? 'linear-gradient(135deg, #1565c0 0%, #42a5f5 100%)' : 'linear-gradient(135deg, #90caf9 0%, #42a5f5 100%)', boxShadow: dragOver ? '0 4px 14px rgba(25,118,210,0.4)' : '0 2px 8px rgba(25,118,210,0.2)', transition: 'all 0.2s ease', mb: 0.5 }}>
                <ImageIcon sx={{ color: 'white', fontSize: 26 }} />
              </Box>
              <Typography variant="body2" fontWeight={700} color={dragOver ? 'primary.main' : 'primary.700'}>
                {t.imageNone}
              </Typography>
              <Typography variant="caption" color="text.secondary" textAlign="center" sx={{ lineHeight: 1.4 }}>
                {t.imageFormat}
              </Typography>
            </Box>
          )}
          {!uploading && (
            <>
              <input type="file" id={inputId} accept="image/jpeg,image/png" style={{ display: 'none' }} onChange={handleFile} />
              <label htmlFor={inputId}>
                <Button component="span" size="small" variant={preview ? 'outlined' : 'contained'} startIcon={<ImageIcon />}
                  sx={{ borderRadius: 2, fontWeight: 600, px: 2.5, background: preview ? undefined : 'linear-gradient(135deg, #1565c0 0%, #1976d2 50%, #42a5f5 100%)', boxShadow: preview ? undefined : '0 3px 10px rgba(25,118,210,0.35)', '&:hover': { boxShadow: preview ? undefined : '0 5px 16px rgba(25,118,210,0.45)', transform: 'translateY(-1px)' }, transition: 'all 0.18s' }}>
                  {preview ? t.imageChange : t.imageAdd}
                </Button>
              </label>
            </>
          )}
        </Box>
      )}
    </Box>
  );
});
QuestionImageUploadInner.displayName = 'QuestionImageUploadInner';

/**
 * QuestionImageUpload
 * Muestra el bloque de carga bloqueado si !subscriptionActive,
 * o el uploader funcional en caso contrario.
 */
export const QuestionImageUpload = memo(({ qId, imageUrl = '', onImageChange, subscriptionActive, t }) => {
  if (!subscriptionActive) {
    return (
      <Box sx={{ mb: 2, borderRadius: 2, overflow: 'hidden', border: '1.5px dashed', borderColor: 'warning.400', background: 'linear-gradient(135deg, rgba(255,152,0,0.06) 0%, rgba(255,193,7,0.10) 50%, rgba(255,152,0,0.06) 100%)', position: 'relative' }}>
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%)', backgroundSize: '200% 100%', animation: 'shimmer 2.8s ease-in-out infinite', pointerEvents: 'none', borderRadius: 'inherit' }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.5 }}>
          <Box sx={{ width: 46, height: 46, borderRadius: 2, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #ff9800 0%, #ffc107 100%)', boxShadow: '0 3px 10px rgba(255,152,0,0.35)', animation: 'floatIcon 2.5s ease-in-out infinite' }}>
            <ImageIcon sx={{ color: 'white', fontSize: 26 }} />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
              <Typography variant="body2" fontWeight={700} color="warning.800" noWrap>
                {t.imageUploadTitle}
              </Typography>
              <AutoAwesomeIcon sx={{ fontSize: 13, color: 'warning.600' }} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4, display: 'block' }}>
              {t.imageLockedDescription}
            </Typography>
          </Box>
          <Chip
            icon={<LockIcon sx={{ fontSize: '0.75rem !important' }} />}
            label={t.imageLockedBadge}
            size="small"
            sx={{ flexShrink: 0, height: 22, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'warning.100', color: 'warning.800', border: '1px solid', borderColor: 'warning.300', '& .MuiChip-icon': { color: 'warning.700' }, '& .MuiChip-label': { px: 0.75 } }}
          />
        </Box>
        <Box sx={{ px: 2, py: 0.6, bgcolor: 'rgba(255,152,0,0.10)', borderTop: '1px dashed', borderColor: 'warning.300', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <StarIcon sx={{ fontSize: 11, color: 'warning.600' }} />
          <Typography variant="caption" sx={{ color: 'warning.700', fontWeight: 600, fontSize: '0.63rem', letterSpacing: 0.3 }}>
            {t.imageLockedCta}
          </Typography>
        </Box>
      </Box>
    );
  }
  return <QuestionImageUploadInner qId={qId} imageUrl={imageUrl} onImageChange={onImageChange} t={t} />;
});
QuestionImageUpload.displayName = 'QuestionImageUpload';

// ─────────────────────────────────────────────────────────────────────────────
// QUESTION FORM
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Questions
 * Tarjeta de edición de una pregunta individual.
 * Reutilizable tanto en la lista de edición como dentro de modales.
 *
 * Props:
 *   qId               string
 *   question          object
 *   handleQuestionChange (qId, field, value) => void
 *   errors            object
 *   removeQuestion    (qId) => void
 *   isOnlyQuestion    boolean
 *   onMoveUp          (qId) => void
 *   onMoveDown        (qId) => void
 *   isFirst           boolean
 *   isLast            boolean
 *   inputRefs         React.MutableRefObject<{}>
 *   handleMarkChanged () => void   (marca dirty sin leer valor)
 *   clearError        (field, qId?) => void
 *   t                 object       (translations)
 *   isNew             boolean      (activa animación de entrada)
 *   subscriptionActive boolean
 */
export const Questions = memo(({
  qId, question, handleQuestionChange, errors, removeQuestion, isOnlyQuestion,
  onMoveUp, onMoveDown, isFirst, isLast, inputRefs, handleMarkChanged, clearError,
  t, isNew, subscriptionActive,
}) => {
  const textRef = useRef(null);

  useEffect(() => {
    const key = `question_text_${qId}`;
    inputRefs.current[key] = textRef.current;
    return () => delete inputRefs.current[key];
  }, [inputRefs, qId]);

  const handleTypeChange = useCallback(
    (e) => {
      const newType = e.target.value;
      const updates = { type: newType };
      if (newType === 'radio'      && !question.options) updates.options = [];
      if (newType === 'slider')                          updates.sliderConfig = { min: 1, max: 5 };
      if (newType === 'emojislike' && !question.config)  updates.config = t.defaultEmojiLikeConfig;
      else if (EMOJI_TYPES.has(newType) && !question.config) updates.config = t.defaultEmojiConfig;
      Object.entries(updates).forEach(([field, value]) => handleQuestionChange(qId, field, value));
    },
    [qId, handleQuestionChange, question, t],
  );

  const handleTextBlur = useCallback(() => {
    if (getQuestionError(errors, qId, 'text') && textRef.current?.value?.trim()) clearError('text', qId);
  }, [errors, qId, clearError]);

  const isEmojiType = useMemo(() => EMOJI_TYPES.has(question.type), [question.type]);

  // Excluye 'text' — igual que en shared.js original
  const filteredQuestionTypes = useMemo(
    () => t.questionTypes.filter((type) => type.value !== 'text'),
    [t.questionTypes],
  );

  const handleImageChange = useCallback(
    (url) => handleQuestionChange(qId, 'url', url),
    [qId, handleQuestionChange],
  );

  return (
    <Card
      elevation={1}
      data-question-id={qId}
      sx={{
        mb: 2,
        overflow: 'visible',
        transition: 'box-shadow 0.2s, transform 0.15s',
        animation: isNew ? 'questionSlideIn 0.28s ease-out' : 'none',
        '&:hover': { boxShadow: 4, transform: 'translateY(-1px)' },
        background: 'transparent',
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <QuestionImageUpload
          qId={qId}
          imageUrl={question.url || ''}
          onImageChange={handleImageChange}
          subscriptionActive={subscriptionActive}
          t={t}
        />

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
          <Typography variant="h6" sx={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'primary.light', color: 'primary.contrastText', borderRadius: '50%', fontSize: '1rem', fontWeight: 'bold', flexShrink: 0 }}>
            {question.orden}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, flexShrink: 0 }}>
            <Tooltip title={isFirst ? t.firstQuestion : t.moveUpTooltip}>
              <span>
                <IconButton size="small" onClick={() => onMoveUp(qId)} disabled={isFirst} sx={{ p: 0.25 }}>
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={isLast ? t.lastQuestion : t.moveDownTooltip}>
              <span>
                <IconButton size="small" onClick={() => onMoveDown(qId)} disabled={isLast} sx={{ p: 0.25 }}>
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <TextField
            fullWidth label={t.questionTextLabel} inputRef={textRef}
            defaultValue={question.text} onChange={handleMarkChanged}
            onBlur={handleTextBlur}
            error={!!getQuestionError(errors, qId, 'text')}
            helperText={getQuestionError(errors, qId, 'text')}
            required size="small" multiline minRows={1} maxRows={3} sx={{ minWidth: 0 }}
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <FormControl sx={{ flexGrow: 1, minWidth: 180, maxWidth: '60%' }} size="small">
            <InputLabel>{t.questionTypeLabel}</InputLabel>
            <Select value={question.type} label={t.questionTypeLabel} onChange={handleTypeChange}>
              {filteredQuestionTypes.map((type) => (
                <MenuItem key={type.value} value={type.value} dense>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {type.icon}
                    <Typography variant="body2">{type.label}</Typography>
                    {EMOJI_TYPES.has(type.value) && <EmojiPreview emojiType={type.value} />}
                    <Typography variant="caption" color="text.secondary">{type.description}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={question.required}
                onChange={(e) => handleQuestionChange(qId, 'required', e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2">{t.requiredLabel}</Typography>
                <Tooltip title={t.requiredTooltip}>
                  <HelpOutlineIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </Tooltip>
              </Box>
            }
            sx={{ m: 0, flexShrink: 0 }}
          />
          <Tooltip title={isOnlyQuestion ? t.minOneQuestion : t.deleteTooltip}>
            <span>
              <IconButton color="error" onClick={() => removeQuestion(qId)} disabled={isOnlyQuestion} size="small">
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {question.type === 'radio' && (
          <RadioOptionsEdit
            qId={qId} options={question.options}
            handleOptionChange={handleQuestionChange}
            errors={errors} inputRefs={inputRefs}
            handleMarkChanged={handleMarkChanged} t={t}
          />
        )}
        {question.type === 'slider' && (
          <SliderConfig
            qId={qId}
            errors={errors} t={t}
          />
        )}
        {isEmojiType && (
          <EmojiTextConfig
            qId={qId} config={question.config}
            handleEmojiConfigChange={handleQuestionChange}
            errors={errors} inputRefs={inputRefs}
            handleMarkChanged={handleMarkChanged} t={t}
          />
        )}
      </CardContent>
    </Card>
  );
});
Questions.displayName = 'Questions';

// ─────────────────────────────────────────────────────────────────────────────
// QUESTIONS SECTION — lista ordenable de QuestionForm
// ─────────────────────────────────────────────────────────────────────────────

/**
 * QuestionsSection
 * Renderiza la lista completa de preguntas con soporte de reordenamiento.
 *
 * Props:
 *   questions             object  { [qId]: question }
 *   errors                object
 *   onQuestionChange      (qId, field, value) => void
 *   onRemoveQuestion      (qId) => void
 *   inputRefs             React.MutableRefObject<{}>
 *   handleMarkChanged     () => void
 *   clearError            (field, qId?) => void
 *   t                     object
 *   lastAddedQuestionId   string | null
 *   subscriptionActive    boolean
 */
export const QuestionsSection = memo(({
  questions, errors, onQuestionChange, onRemoveQuestion, inputRefs, handleMarkChanged,
  clearError, t, lastAddedQuestionId, subscriptionActive,
}) => {
  const sortedQuestions = useMemo(
    () => Object.entries(questions).sort(([, a], [, b]) => a.orden - b.orden),
    [questions],
  );

  const orderToId = useMemo(() => {
    const map = new Map();
    for (const [id, q] of Object.entries(questions)) map.set(q.orden, id);
    return map;
  }, [questions]);

  const handleMoveUp = useCallback(
    (qId) => {
      const cur = questions[qId].orden;
      if (cur <= 1) return;
      const prevId = orderToId.get(cur - 1);
      if (!prevId) return;
      onQuestionChange(qId, 'orden', cur - 1);
      onQuestionChange(prevId, 'orden', cur);
    },
    [questions, orderToId, onQuestionChange],
  );

  const handleMoveDown = useCallback(
    (qId) => {
      const cur   = questions[qId].orden;
      const total = Object.keys(questions).length;
      if (cur >= total) return;
      const nextId = orderToId.get(cur + 1);
      if (!nextId) return;
      onQuestionChange(qId, 'orden', cur + 1);
      onQuestionChange(nextId, 'orden', cur);
    },
    [questions, orderToId, onQuestionChange],
  );

  return (
    <>
      <Box sx={{ my: 3, display: 'flex', alignItems: 'center' }}>
        <Typography variant="h5" component="h2" sx={{ flexGrow: 1, fontWeight: 'medium' }}>
          {t.questionsTitle}
        </Typography>
      </Box>
      <Box sx={{ mt: 1 }}>
        {sortedQuestions.map(([qId, question], idx) => (
          <Questions
            key={qId}
            qId={qId}
            question={question}
            handleQuestionChange={onQuestionChange}
            errors={errors}
            removeQuestion={onRemoveQuestion}
            isOnlyQuestion={sortedQuestions.length <= 1}
            onMoveUp={handleMoveUp}
            onMoveDown={handleMoveDown}
            isFirst={idx === 0}
            isLast={idx === sortedQuestions.length - 1}
            inputRefs={inputRefs}
            handleMarkChanged={handleMarkChanged}
            clearError={clearError}
            t={t}
            isNew={qId === lastAddedQuestionId}
            subscriptionActive={subscriptionActive}
          />
        ))}
      </Box>
    </>
  );
});
QuestionsSection.displayName = 'QuestionsSection';